/* import __COLOCATED_TEMPLATE__ from './content-redaction-rules.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type SecurityAppSettings from 'embercom/models/security-app-settings';

interface RedactionRule {
  id: string;
  name: string;
  type: 'system' | 'custom';
  enabled: boolean;
  pattern: string;
  validator: string | null;
}

interface Args {
  settings: SecurityAppSettings;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

const MAX_CUSTOM_RULES = 10;

export default class ContentRedactionRules extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked showRuleModal = false;
  @tracked editingRuleId: string | null = null;
  @tracked ruleNameInput = '';
  @tracked rulePatternInput = '';
  @tracked testStringInput = '';
  @tracked patternError: string | null = null;
  @tracked showDeleteConfirmation = false;
  @tracked ruleToDelete: RedactionRule | null = null;

  // Local tracked copy of rules — decoupled from Ember Data's flaky array tracking.
  // Initialized lazily from the model, all mutations go through this, and
  // every mutation syncs back to the model for serialization.
  @tracked _localRules: RedactionRule[] | null = null;

  get rules(): RedactionRule[] {
    if (this._localRules !== null) {
      return this._localRules;
    }
    return this.args.settings.conversationRedactionRules ?? [];
  }

  get isRedactionEnabled(): boolean {
    return this.args.settings.conversationRedactionEnabled ?? false;
  }

  get systemRules(): RedactionRule[] {
    return this.rules.filter((rule: RedactionRule) => rule.type === 'system');
  }

  get customRules(): RedactionRule[] {
    return this.rules.filter((rule: RedactionRule) => rule.type === 'custom');
  }

  get canAddCustomRule(): boolean {
    return this.customRules.length < MAX_CUSTOM_RULES;
  }

  get customRulesCount(): string {
    return this.intl.t('apps.app.settings.security.content-redaction.custom-rules-count', {
      count: this.customRules.length,
    });
  }

  get isEditingRule(): boolean {
    return this.editingRuleId !== null;
  }

  get modalTitle(): string {
    return this.isEditingRule
      ? this.intl.t('apps.app.settings.security.content-redaction.edit-rule')
      : this.intl.t('apps.app.settings.security.content-redaction.add-rule');
  }

  get testMatches(): string[] {
    if (!this.rulePatternInput || !this.testStringInput) {
      return [];
    }
    try {
      let regex = new RegExp(this.rulePatternInput, 'g');
      let matches = this.testStringInput.match(regex);
      return matches ?? [];
    } catch (_e: unknown) {
      return [];
    }
  }

  get redactedPreview(): string {
    if (!this.rulePatternInput || !this.testStringInput) {
      return '';
    }
    try {
      let regex = new RegExp(this.rulePatternInput, 'g');
      return this.testStringInput.replace(regex, '[REDACTED]');
    } catch (_e: unknown) {
      return this.testStringInput;
    }
  }

  get isPatternValid(): boolean {
    if (!this.rulePatternInput) {
      return true;
    }
    try {
      new RegExp(this.rulePatternInput);
      return true;
    } catch (_e: unknown) {
      return false;
    }
  }

  get canSaveRule(): boolean {
    return (
      this.ruleNameInput.trim().length > 0 &&
      this.rulePatternInput.trim().length > 0 &&
      this.isPatternValid
    );
  }

  private updateRules(updatedRules: RedactionRule[]) {
    // Update local tracked state (drives the template)
    this._localRules = updatedRules;
    // Write to staging property on the model — the serializer reads from here,
    // bypassing Ember Data's broken @attr array dirty-tracking
    (this.args.settings as any)._pendingRedactionRules = updatedRules;
    // Also use .set() to trigger hasDirtyAttributes so the Save button activates
    this.args.settings.set('conversationRedactionRules', updatedRules);
  }

  @action
  toggleRedaction() {
    let newValue = !this.args.settings.conversationRedactionEnabled;
    this.args.settings.set('conversationRedactionEnabled', newValue);
    this.args.settings.conversationRedactionEnabled = newValue;
  }

  @action
  toggleSystemRule(ruleId: string) {
    let updatedRules = this.rules.map((rule: RedactionRule) => {
      if (rule.id === ruleId) {
        return { ...rule, enabled: !rule.enabled };
      }
      return rule;
    });
    this.updateRules(updatedRules);
  }

  @action
  toggleCustomRule(ruleId: string) {
    let updatedRules = this.rules.map((rule: RedactionRule) => {
      if (rule.id === ruleId) {
        return { ...rule, enabled: !rule.enabled };
      }
      return rule;
    });
    this.updateRules(updatedRules);
  }

  @action
  openAddRuleModal() {
    this.editingRuleId = null;
    this.ruleNameInput = '';
    this.rulePatternInput = '';
    this.testStringInput = '';
    this.patternError = null;
    this.showRuleModal = true;
  }

  @action
  openEditRuleModal(rule: RedactionRule) {
    this.editingRuleId = rule.id;
    this.ruleNameInput = rule.name;
    this.rulePatternInput = rule.pattern;
    this.testStringInput = '';
    this.patternError = null;
    this.showRuleModal = true;
  }

  @action
  closeRuleModal() {
    this.showRuleModal = false;
    this.editingRuleId = null;
    this.ruleNameInput = '';
    this.rulePatternInput = '';
    this.testStringInput = '';
    this.patternError = null;
  }

  @action
  saveRule() {
    if (!this.canSaveRule) {
      return;
    }

    if (!this.isPatternValid) {
      this.patternError = this.intl.t(
        'apps.app.settings.security.content-redaction.invalid-pattern',
      );
      return;
    }

    let updatedRules: RedactionRule[];

    if (this.isEditingRule) {
      updatedRules = this.rules.map((rule: RedactionRule) => {
        if (rule.id === this.editingRuleId) {
          return {
            ...rule,
            name: this.ruleNameInput.trim(),
            pattern: this.rulePatternInput.trim(),
          };
        }
        return rule;
      });
    } else {
      let newRule: RedactionRule = {
        id: `custom_${this.generateId()}`,
        name: this.ruleNameInput.trim(),
        type: 'custom',
        enabled: true,
        pattern: this.rulePatternInput.trim(),
        validator: null,
      };
      updatedRules = [...this.rules, newRule];
    }

    this.updateRules(updatedRules);
    this.closeRuleModal();
  }

  @action
  confirmDeleteRule(rule: RedactionRule) {
    this.ruleToDelete = rule;
    this.showDeleteConfirmation = true;
  }

  @action
  cancelDelete() {
    this.ruleToDelete = null;
    this.showDeleteConfirmation = false;
  }

  @action
  deleteRule() {
    if (!this.ruleToDelete) {
      return;
    }

    let updatedRules = this.rules.filter(
      (rule: RedactionRule) => rule.id !== this.ruleToDelete!.id,
    );
    this.updateRules(updatedRules);
    this.ruleToDelete = null;
    this.showDeleteConfirmation = false;
  }

  @action
  updateRuleName(event: Event) {
    this.ruleNameInput = (event.target as HTMLInputElement).value;
  }

  @action
  updateRulePattern(event: Event) {
    this.rulePatternInput = (event.target as HTMLInputElement).value;
    this.patternError = null;
  }

  @action
  updateTestString(event: Event) {
    this.testStringInput = (event.target as HTMLInputElement).value;
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 10);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Security::Tabs::ContentRedactionRules': typeof ContentRedactionRules;
    'new-settings/workspace/security/tabs/content-redaction-rules': typeof ContentRedactionRules;
  }
}
